import styled from 'styled-components';
import React, { useState, useCallback, useEffect } from 'react';
import { Table } from 'react-virtualized';
import unescape from 'lodash/unescape';
import debounce from 'lodash/debounce';

const Style = styled.div`
    position: relative;

    box-shadow: rgb(255 255 255 / 80%) 0px 5px 25px 5px;
    background-color: #fff;

    .ReactVirtualized__Table {
        .ReactVirtualized__Table__Grid {
            outline: none;
        }

        .ReactVirtualized__Table__row {
            .item {
                height: 100%;
                padding: 5px;
                position: relative;

                .textarea {
                    border: none;
                    width: 100%;
                    height: 100%;
                    font-size: 12px;
                    padding: 10px;
                    text-align: center;
                    background-color: #422399d1;
                    border-radius: 10px;
                    border: 1px solid #422399;
                    transition: all 0.2s ease;
                    resize: none;
                    outline: none;
                    color: white;

                    &.highlight {
                        background-color: #232f99;
                        border: 1px solid rgba(255, 255, 255, 0.3);
                        color: white;
                    }

                    &.illegal {
                        background-color: rgb(123 29 0);
                        border: 1px solid rgba(255, 255, 255, 0.3);
                    }
                }

                .cue-meta {
                    position: absolute;
                    right: 12px;
                    bottom: 10px;
                    font-size: 10px;
                    line-height: 1;
                    color: rgb(255 255 255 / 75%);
                    background-color: rgb(0 0 0 / 45%);
                    padding: 2px 6px;
                    border-radius: 7px;
                    pointer-events: none;

                    .sep {
                        opacity: 0.5;
                        margin: 0 1px;
                    }

                    /* Only the line(s) actually over the limit turn red. */
                    .over {
                        color: #ff8a87;
                        font-weight: 700;
                    }
                }
            }
        }
    }
`;

export default function Subtitles({ currentIndex, subtitle, checkSub, player, updateSub, maxChars, maxLines }) {
    const [height, setHeight] = useState(100);

    const resize = useCallback(() => {
        setHeight(document.body.clientHeight - 170);
    }, [setHeight]);

    useEffect(() => {
        resize();
        if (!resize.init) {
            resize.init = true;
            const debounceResize = debounce(resize, 500);
            window.addEventListener('resize', debounceResize);
        }
    }, [resize]);

    return (
        <Style className="subtitles">
            <Table
                headerHeight={40}
                width={250}
                height={height}
                rowHeight={80}
                scrollToIndex={currentIndex}
                rowCount={subtitle.length}
                rowGetter={({ index }) => subtitle[index]}
                headerRowRenderer={() => null}
                rowRenderer={(props) => {
                    const rawText = unescape(props.rowData.text || '');
                    const lineLens = rawText.split('\n').map((line) => line.length);
                    const lineCount = lineLens.length;
                    return (
                        <div
                            key={props.key}
                            className={props.className}
                            style={props.style}
                            onClick={() => {
                                if (player) {
                                    player.pause();
                                    if (player.duration >= props.rowData.startTime) {
                                        player.currentTime = props.rowData.startTime + 0.001;
                                    }
                                }
                            }}
                        >
                            <div className="item">
                                <textarea
                                    maxLength={200}
                                    spellCheck={false}
                                    className={[
                                        'textarea',
                                        currentIndex === props.index ? 'highlight' : '',
                                        checkSub(props.rowData) ? 'illegal' : '',
                                    ]
                                        .join(' ')
                                        .trim()}
                                    value={unescape(props.rowData.text)}
                                    onChange={(event) => {
                                        updateSub(props.rowData, {
                                            text: event.target.value,
                                        });
                                    }}
                                />
                                {/* Limits come from the file's NOTE layout; without it
                                    (old files) only the counts show, never red. */}
                                <div className="cue-meta">
                                    {lineLens.map((len, i) => (
                                        <React.Fragment key={i}>
                                            {i > 0 ? <span className="sep">·</span> : null}
                                            <span className={maxChars && len > maxChars ? 'over' : ''}>{len}</span>
                                        </React.Fragment>
                                    ))}
                                    {maxChars ? `/${maxChars}` : ''}c ·{' '}
                                    <span className={maxLines && lineCount > maxLines ? 'over' : ''}>{lineCount}</span>
                                    {maxLines ? `/${maxLines}` : ''}L
                                </div>
                            </div>
                        </div>
                    );
                }}
            ></Table>
        </Style>
    );
}
